import React from "react"
import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import get from "lodash/get"
import { Avatar, Box, Chip, Typography, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"
import {
  getCategoryIcon,
  getFeatureDetail,
  IOccupantDetail,
} from "utils/feature"
import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { ShopIcon } from "components/icons"
import { FeatureListItem } from "../features/ListItem"
import { hasVatRefund } from "extensions/iconsiam/utils"
import { ComingSoonChip, NewShopChip, VatRefundChip } from "../../chips"

const DEFAULT_SEPARATOR = " | "
export const ShopListItem = ({ occupant, hideLevel = false, ...props }) => {
  const theme = useTheme()
  const { t } = useTranslation({ defaultValue: null })
  const { isMultiVenueProject } = useVenue()
  const navigate = useNavigate()

  const styleOverrides =
    theme.components[`FeatureListItem`]?.styleOverrides || {}
  const {
    locationText,
    sponsoredChip,
    chipContainer,
    newShopChip,
    comingSoonChip,
    vatRefundChip,
  } = styleOverrides
  const { locationTextSeparator = DEFAULT_SEPARATOR } =
    theme.components[`FeatureListItem`]?.defaultProps || {}

  const {
    name,
    logo,
    level,
    group,
    zone,
    venueLogo,
    venueRef,
    isAd,
    allLevels,
  } = useMemo(() => getFeatureDetail(occupant), [occupant]) as IOccupantDetail
  const handleClick = useCallback(
    () => navigate(`/maps/place/${occupant.id}`),
    [navigate, occupant?.id]
  )
  const { isNewShop, isComingSoon } = occupant?.properties
  const isVatRefund = useMemo(() => hasVatRefund(occupant), [occupant])
  const zoneText = useMemo(() => t(zone), [t, zone])

  const categoryIcon = useMemo(() => {
    const parentCategory = get(occupant, "properties.local_categories[0]", {})
    return getCategoryIcon(parentCategory)
  }, [occupant])

  const groupText = useMemo(() => t(group), [t, group])

  const allLevelDisplay = useMemo(
    () => allLevels?.join(", ") || "-",
    [allLevels]
  )

  return (
    <FeatureListItem
      divider
      className={venueRef}
      onClick={handleClick}
      startAdornment={
        <Box sx={chipContainer}>
          {isComingSoon && !isAd && (
            <ComingSoonChip sx={comingSoonChip} label={t("Coming soon")} />
          )}
          {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
          {isVatRefund && <VatRefundChip sx={vatRefundChip} />}
        </Box>
      }
      primary={
        <>
          {isAd ? (
            <Chip label={t("sponsored.search_result")} sx={sponsoredChip} />
          ) : null}
          {t(name)}
        </>
      }
      secondary={
        occupant.hasMultipleLocations ? (
          <Typography component="span">{t("Multiple Locations")}</Typography>
        ) : (
          <React.Fragment>
            {/* // TODO: Refactor secondary text display to allow configuration via theme. */}
            <Typography component="span" sx={locationText}>
              {_.compact([
                !hideLevel &&
                  allLevelDisplay &&
                  `${t("At Floor", {
                    value: allLevelDisplay,
                  })}`,
                !hideLevel && allLevelDisplay && zoneText,
                zoneText && `${t("Zone")} ${zoneText} `,
                group && groupText,
              ]).join(locationTextSeparator)}
            </Typography>
          </React.Fragment>
        )
      }
      avatar={
        <Avatar src={logo}>
          {!logo &&
            (getIcon(categoryIcon, null, {
              imageIconProps: {
                solidColorFilterId: "directoryCard_avatar_filter",
              },
            }) || <ShopIcon />)}
        </Avatar>
      }
      endAdornmentSlotClasses={{
        text: venueRef,
      }}
      endAdornmentSlots={{
        text: t(level),
        media:
          !occupant.hasMultipleLocations && isMultiVenueProject && venueLogo,
      }}
      {...props}
    />
  )
}

export default ShopListItem
